import React, { useRef, useEffect, useCallback } from 'react';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  error?: boolean;
}

const EMPTY_VALUES = ['', '<br>', '<div><br></div>', '<p><br></p>'];

const isEmptyHtml = (html: string) => EMPTY_VALUES.includes(html.trim());

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Rédigez vos observations...',
  minHeight = 200,
  error = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isInternalChange = useRef(false);

  // Sync prop → DOM uniquement si la valeur vient de l'extérieur
  useEffect(() => {
    const el = editorRef.current;
    if (!el || isInternalChange.current) return;
    const current = el.innerHTML;
    if (current !== value) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const exec = useCallback(
    (command: string, val?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, val ?? undefined);
      if (editorRef.current) {
        isInternalChange.current = true;
        onChange(editorRef.current.innerHTML);
        requestAnimationFrame(() => { isInternalChange.current = false; });
      }
    },
    [onChange]
  );

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      isInternalChange.current = true;
      onChange(editorRef.current.innerHTML);
      requestAnimationFrame(() => { isInternalChange.current = false; });
    }
  }, [onChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      exec('insertHTML', '&nbsp;&nbsp;&nbsp;&nbsp;');
    }
  };

  const ToolBtn: React.FC<{
    title: string;
    onClick: () => void;
    active?: boolean;
    children: React.ReactNode;
    className?: string;
  }> = ({ title, onClick, active, children, className }) => (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={`px-2 py-1.5 rounded text-sm transition-colors
        ${active
          ? 'bg-primary-100 text-primary-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}
        ${className ?? ''}`}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="w-px h-5 bg-gray-200 mx-0.5 self-center" />;

  return (
    <div
      className={`rounded-xl border overflow-hidden transition-colors
        ${error
          ? 'border-red-400'
          : 'border-gray-200 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500/20'
        }`}
    >
      {/* ── Toolbar ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200">

        {/* Titres */}
        <select
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => { exec('formatBlock', e.target.value); e.target.value = ''; }}
          defaultValue=""
          className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer"
        >
          <option value="" disabled>Style</option>
          <option value="p">Paragraphe</option>
          <option value="h1">Titre 1</option>
          <option value="h2">Titre 2</option>
          <option value="h3">Titre 3</option>
        </select>

        <Divider />

        {/* Gras / Italique / Souligné / Barré */}
        <ToolBtn title="Gras (Ctrl+B)" onClick={() => exec('bold')}>
          <span className="font-bold">G</span>
        </ToolBtn>
        <ToolBtn title="Italique (Ctrl+I)" onClick={() => exec('italic')}>
          <span className="italic">I</span>
        </ToolBtn>
        <ToolBtn title="Souligné (Ctrl+U)" onClick={() => exec('underline')}>
          <span className="underline">S</span>
        </ToolBtn>
        <ToolBtn title="Barré" onClick={() => exec('strikeThrough')}>
          <span className="line-through">T</span>
        </ToolBtn>

        <Divider />

        {/* Listes */}
        <ToolBtn title="Liste à puces" onClick={() => exec('insertUnorderedList')}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            <circle cx="2" cy="6" r="1" fill="currentColor" />
            <circle cx="2" cy="10" r="1" fill="currentColor" />
            <circle cx="2" cy="14" r="1" fill="currentColor" />
            <circle cx="2" cy="18" r="1" fill="currentColor" />
          </svg>
        </ToolBtn>
        <ToolBtn title="Liste numérotée" onClick={() => exec('insertOrderedList')}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M8 6h13M8 12h13M8 18h13" />
            <path strokeLinecap="round" strokeWidth={1.5} d="M3 6h.01M3 12h.01M3 18h.01" />
          </svg>
        </ToolBtn>

        <Divider />

        {/* Alignements */}
        <ToolBtn title="Aligner à gauche" onClick={() => exec('justifyLeft')}>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2 4h16v1.5H2V4zm0 4h10v1.5H2V8zm0 4h16v1.5H2V12zm0 4h10v1.5H2V16z" clipRule="evenodd" />
          </svg>
        </ToolBtn>
        <ToolBtn title="Centrer" onClick={() => exec('justifyCenter')}>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2 4h16v1.5H2V4zm3 4h10v1.5H5V8zm-3 4h16v1.5H2V12zm3 4h10v1.5H5V16z" clipRule="evenodd" />
          </svg>
        </ToolBtn>
        <ToolBtn title="Aligner à droite" onClick={() => exec('justifyRight')}>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M2 4h16v1.5H2V4zm6 4h10v1.5H8V8zm-6 4h16v1.5H2V12zm6 4h10v1.5H8V16z" clipRule="evenodd" />
          </svg>
        </ToolBtn>

        <Divider />

        {/* Indentation */}
        <ToolBtn title="Augmenter l'indentation" onClick={() => exec('indent')}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M3 6h18M3 12h12M3 18h18M9 9l3 3-3 3" />
          </svg>
        </ToolBtn>
        <ToolBtn title="Réduire l'indentation" onClick={() => exec('outdent')}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M3 6h18M9 12h12M3 18h18M9 9l-3 3 3 3" />
          </svg>
        </ToolBtn>

        <Divider />

        {/* Lien */}
        <ToolBtn
          title="Insérer un lien"
          onClick={() => {
            const url = window.prompt('URL du lien :');
            if (url) exec('createLink', url);
          }}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        </ToolBtn>

        <Divider />

        {/* Effacer le formatage */}
        <ToolBtn title="Effacer le formatage" onClick={() => exec('removeFormat')} className="ml-auto">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </ToolBtn>
      </div>

      {/* ── Zone d'édition ──────────────────────────────────────── */}
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          style={{ minHeight }}
          className="
            px-4 py-3 text-sm text-gray-800 leading-relaxed focus:outline-none
            prose prose-sm max-w-none
            [&_h1]:text-xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mb-2 [&_h1]:mt-3
            [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-gray-800 [&_h2]:mb-1.5 [&_h2]:mt-2.5
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-700 [&_h3]:mb-1 [&_h3]:mt-2
            [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5
            [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5
            [&_li]:my-0.5
            [&_a]:text-primary-600 [&_a]:underline
            [&_strong]:font-bold [&_em]:italic [&_u]:underline [&_s]:line-through
            [&_p]:my-1
          "
        />
        {/* Placeholder */}
        {isEmptyHtml(value ?? '') && (
          <div
            className="absolute top-3 left-4 text-sm text-gray-400 pointer-events-none select-none"
            aria-hidden
          >
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
};

export default RichTextEditor;
